import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  DateUI,
  Input,
  Label,
  PrimaryFullScreenSpinner,
  toast,
  UserProfile,
} from "@repo/ui";
import { neverFunc } from "@repo/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, Suspense, useState } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { FileImportKind } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const macquarieHeaders = [
  "Account number",
  "Dealer Adviser Code",
  "Account Type (Product Code)",
  "Account Name",
  "Date Of Transaction",
  "Category",
  "Description",
  "Debit (Amount)",
  "Credit (Amount)",
] as const;

const westpacHeaders = [
  "Account description",
  "Account number",
  "Currency",
  "Date",
  "Description of transaction",
  "Debits",
  "Credits",
  "Balance",
] as const;

export const Route = createFileRoute("/__auth/transaction-import")({
  head: () => opsHead("Transaction Import"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();

  graphql(`
    fragment AdminTransactionImportFile on ImportFile {
      __typename
      id
      shortRef
      fileName
      uploadedAt
      createdByUser {
        id
        ...UserProfileUI
      }
    }
  `);

  const [createFileMutation] = useMutation(
    graphql(`
      mutation CreateTransactionImportFile($input: CreateImportFileInput!) {
        createImportFile(input: $input) {
          importFile {
            id
            uploadUrl
          }
        }
      }
    `),
  );

  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileUpload = async () => {
    const file = selectedFile;
    if (!file) {
      return;
    }

    try {
      setUploading(true);

      const csvFileContents = await file.text();
      const detectedImportKind =
        detectImportKindFromCsvContents(csvFileContents);

      const result = await createFileMutation({
        variables: {
          input: {
            fileName: file.name,
            kind: detectedImportKind,
          },
        },
      });
      const importFile = result.data?.createImportFile?.importFile;
      if (!importFile) {
        throw new Error("expected import file in payload");
      }

      const uploadUrl = importFile.uploadUrl;
      if (!uploadUrl) {
        throw new Error("expected uploadUrl");
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "text/csv",
        },
      });
      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }

      void navigate({
        to: "/transaction-import/$fileImportId",
        params: { fileImportId: importFile.id },
      });
    } catch (error) {
      toast({
        title: "Error uploading file",
        description: String(error),
        kind: "error",
        autoHide: false,
      });
    } finally {
      setUploading(false);
    }
  };

  const filesDoc = graphql(`
    query AdminTransactionImportFileList($after: String, $first: Int!, $order: ImportFilePageOrder!) {
      importFiles(after: $after, first: $first, order: $order) {
        pageInfo {
          endCursor
          hasNextPage
        }
        importFiles {
          ...AdminTransactionImportFile
        }
      }
    }
  `);

  const baseInput = {
    first: 20,
    order: "DATE_DESC" as const,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(filesDoc, {
    variables: {
      after: null,
      ...baseInput,
    },
    notifyOnNetworkStatusChange: true,
  });

  const nextCursor = data?.importFiles?.pageInfo.hasNextPage
    ? (data.importFiles.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <AdminPageContainer testId="admin-transaction-import">
      <AdminPageHeader
        title="Transaction import"
        subtitle="Import banking transaction data for Westpac or Macquarie accounts"
      />

      <div className="flex flex-col space-y-6">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await handleFileUpload();
          }}
          className="flex flex-col items-start space-y-2"
        >
          <Label htmlFor="csvFile">Upload new CSV file</Label>
          <div className="flex flex-row space-x-3">
            <div>
              <Input
                id="csvFile"
                type="file"
                accept=".csv"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0];
                  setSelectedFile(file ?? null);
                }}
              />
            </div>

            <Button
              variant="primary"
              type="submit"
              disabled={!selectedFile || uploading}
              loading={uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </form>

        <div>
          <h2 className="pb-1">File import history</h2>

          <DataTable
            data={data?.importFiles.importFiles ?? []}
            manualSorting={true}
            manualPagination={true}
            maxMultiSortColCount={1}
            hidePagination={true}
            pageSize={10_000}
            className="overflow-hidden border"
            isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
            linkTo={(row) => `/transaction-import/${row.original.id}`}
            columns={[
              {
                accessorKey: "id",
                header: "ID",
                cell: ({ row }) => {
                  return (
                    <Link
                      to="/transaction-import/$fileImportId"
                      params={{ fileImportId: row.original.id }}
                    >
                      {row.original.id}
                    </Link>
                  );
                },
              },
              {
                accessorKey: "__typename",
                header: "Kind",
                cell: ({ row }) => {
                  switch (row.original.__typename) {
                    case "MacquarieTransactionImportFile":
                      return "Macquarie";
                    case "WestpacTransactionImportFile":
                      return "Westpac";
                    default:
                      return neverFunc(row.original);
                  }
                },
              },
              {
                accessorKey: "fileName",
                header: "File name",
                cell: ({ row }) => {
                  return (
                    <span className="font-mono">{row.original.fileName}</span>
                  );
                },
              },
              {
                accessorKey: "operatorName",
                header: "Operator",
                cell: ({ row }) => {
                  return row.original.createdByUser ? (
                    <UserProfile {...row.original.createdByUser} />
                  ) : (
                    "-"
                  );
                },
              },
              {
                accessorKey: "createdAt",
                header: "Uploaded at",
                cell: ({ row }) => {
                  return (
                    <DateUI
                      date={row.original.uploadedAt}
                      variant="DATETIME_TZ"
                    />
                  );
                },
              },
            ]}
          />
          <div className="mt-4 flex w-full flex-row justify-center">
            {nextCursor ? (
              <Button
                onClick={() =>
                  fetchMore({ variables: { ...baseInput, after: nextCursor } })
                }
                variant="outline"
                loading={loading || networkStatus === NetworkStatus.fetchMore}
              >
                Load more
              </Button>
            ) : (
              !!data?.importFiles.importFiles?.length && (
                <p className="text-secondary text-sm">No more data</p>
              )
            )}
          </div>
        </div>
      </div>
    </AdminPageContainer>
  );
}

function detectImportKindFromCsvContents(
  csvFileContents: string,
): FileImportKind {
  const headerRow = csvFileContents
    .split(/\r?\n/)
    .find((line) => line.trim().length > 0);

  if (!headerRow) {
    throw new Error("CSV is empty");
  }

  const headers = new Set(
    splitCsvRow(headerRow).map((header) => normalizeHeaderValue(header)),
  );

  if (includesAllHeaders(headers, macquarieHeaders)) {
    return "MACQUARIE_TRANSACTION_FILE";
  }

  if (includesAllHeaders(headers, westpacHeaders)) {
    return "WESTPAC_TRANSACTION_FILE";
  }

  throw new Error(
    "CSV header format is unsupported. Please upload a Macquarie or Westpac transaction CSV export.",
  );
}

function includesAllHeaders(
  headers: Set<string>,
  requiredHeaders: readonly string[],
): boolean {
  for (const requiredHeader of requiredHeaders) {
    if (!headers.has(requiredHeader)) {
      return false;
    }
  }
  return true;
}

function normalizeHeaderValue(header: string): string {
  const trimmed = header.trim().replace(/^\uFEFF/, "");
  if (trimmed.startsWith(`"`) && trimmed.endsWith(`"`)) {
    return trimmed.slice(1, -1).replace(/""/g, `"`);
  }
  return trimmed;
}

function splitCsvRow(row: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;
  let i = 0;

  while (i < row.length) {
    const char = row[i];
    if (!char) {
      break;
    }

    if (char === `"`) {
      const nextChar = row[i + 1];
      if (inQuotes && nextChar === `"`) {
        current += `"`;
        i += 2;
        continue;
      }
      inQuotes = !inQuotes;
      i += 1;
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
      i += 1;
      continue;
    }

    current += char;
    i += 1;
  }

  values.push(current);
  return values;
}
